import { XMarkIcon } from "@heroicons/react/24/outline";
import type { ColumnDef } from "@tanstack/react-table";
import { Badge } from "@/core/components/ui/badge";
import { Button } from "@/core/components/ui/button";
import { Stack } from "@/core/components/ui/stack";
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from "@/core/components/ui/tooltip";
import { Typography } from "@/core/components/ui/typography";
import type {
  PayrollRosterDraftMember,
  PayrollRosterMember,
} from "@/payroll/roster/types";
import {
  comparePayrollRosterValues,
  normalizePayrollRosterSortValue,
} from "@/payroll/roster/utils";
import { ROSTER_NAME_COLUMN_ID, ROSTER_NAME_COLUMN_LABEL } from "./constants";
import { rosterColumnHeader } from "./header";
import { RosterCellValue } from "./roster-cell-value";

interface NameColParams {
  editedIds: Set<string>;
  flaggedMembers: Set<PayrollRosterMember>;
  departedIds: Set<string>;
  onOpen: (draft: PayrollRosterDraftMember) => void;
  onDismissSuggestion: (draft: PayrollRosterDraftMember) => void;
  dismissingSuggestionId: string | null;
  busy: boolean;
  width: number;
}

const MISSING_ROSTER_REASON = "Active Volca user who is not on this roster";
const INACTIVE_SERVICE_TITAN_REASON =
  "No longer an active ServiceTitan technician. Remove them unless they should still be paid.";

export const NameCol = ({
  editedIds,
  flaggedMembers,
  departedIds,
  onOpen,
  onDismissSuggestion,
  dismissingSuggestionId,
  busy,
  width,
}: NameColParams): ColumnDef<PayrollRosterDraftMember> => ({
  id: ROSTER_NAME_COLUMN_ID,
  accessorFn: ({ member }) =>
    normalizePayrollRosterSortValue(member.display_name),
  header: rosterColumnHeader(ROSTER_NAME_COLUMN_LABEL),
  sortUndefined: "last",
  sortingFn: (left, right) =>
    comparePayrollRosterValues(
      left.original.member.display_name,
      right.original.member.display_name,
      "intrinsic_text",
    ),
  meta: { size: `${width}px` },
  cell: ({ row }) => {
    const { member, suggestion } = row.original;
    const isDeparted = departedIds.has(member.service_titan_employee_id);
    const statusDescription = suggestion
      ? MISSING_ROSTER_REASON
      : isDeparted
        ? INACTIVE_SERVICE_TITAN_REASON
        : undefined;
    const statusDescriptionId = statusDescription
      ? `roster-status-${row.id}`
      : undefined;
    const personName = member.display_name || "person";
    const dismissing =
      suggestion?.service_titan_employee_id === dismissingSuggestionId;

    return (
      <Stack
        direction="row"
        align="center"
        gap="sm"
        className="min-w-0 max-w-full"
      >
        <button
          type="button"
          aria-label={
            suggestion ? `Add ${personName} to roster` : `Edit ${personName}`
          }
          aria-describedby={statusDescriptionId}
          disabled={busy}
          className="min-w-0 flex-1 overflow-hidden rounded text-left focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
          onClick={(event) => {
            event.stopPropagation();
            onOpen(row.original);
          }}
        >
          <Stack gap="none">
            <RosterCellValue value={member.display_name || "-"} />
            {/* The ServiceTitan ID identifies the person as much as their name
                does, so it rides under it instead of taking a column. */}
            <Typography
              variant="bodyW3"
              textColor="tertiary"
              className="truncate"
            >
              {member.service_titan_employee_id || "-"}
            </Typography>
          </Stack>
        </button>
        {statusDescription && (
          <span id={statusDescriptionId} className="sr-only">
            {statusDescription}
          </span>
        )}
        {suggestion ? (
          <Stack direction="row" align="center" gap="xs" className="shrink-0">
            {/* The reason rides the badge: spelled out inline it truncated on
                every row at every window size. */}
            <Tooltip>
              <TooltipTrigger asChild>
                <Badge variant="red" size="sm" className="shrink-0">
                  Missing
                </Badge>
              </TooltipTrigger>
              <TooltipContent>{MISSING_ROSTER_REASON}</TooltipContent>
            </Tooltip>
            <Tooltip>
              <TooltipTrigger asChild>
                <Button
                  type="button"
                  variant="icon"
                  size="iconSm"
                  aria-label={`Mark ${personName} as not on payroll`}
                  disabled={busy}
                  loading={dismissing}
                  className="text-negative-text hover:bg-red-10"
                  onClick={(event) => {
                    event.stopPropagation();
                    onDismissSuggestion(row.original);
                  }}
                >
                  <XMarkIcon />
                </Button>
              </TooltipTrigger>
              <TooltipContent>Mark as not on payroll</TooltipContent>
            </Tooltip>
          </Stack>
        ) : isDeparted ? (
          // Not a blocker: ServiceTitan no longer lists this person as active,
          // so the roster recommends taking them off rather than refusing to
          // publish. Only the business knows if they are really gone.
          <Tooltip>
            <TooltipTrigger asChild>
              <Badge variant="gold" size="sm" className="shrink-0">
                Inactive
              </Badge>
            </TooltipTrigger>
            <TooltipContent>{INACTIVE_SERVICE_TITAN_REASON}</TooltipContent>
          </Tooltip>
        ) : flaggedMembers.has(member) ? (
          <Badge variant="gold" size="sm" className="shrink-0">
            Needs attention
          </Badge>
        ) : (
          editedIds.has(row.original.id) && (
            <Badge variant="gray" size="sm" className="shrink-0">
              Edited
            </Badge>
          )
        )}
      </Stack>
    );
  },
});
